import * as React from 'react';
import { makeStyles, FormControl, FormLabel, FormGroup, FormControlLabel } from '@material-ui/core';
import TextStyles, { fontFamilies } from '../theme/text';
import { neutral } from '../theme/colors';
import { TertiaryButton } from '../buttons';
import { StyledCheckbox } from '../inputs';

interface FilterGroupProps {
  /** array of possible filter options */
  filters: { id: string | number; name: string }[];
  /** display name of collection of filters */
  title: string;
  /** currently selected filter ids */
  selected: Array<string | number>;
  updateSelectedFilters: (title: string, selected: Array<string | number>) => void;
}

const useStyles = makeStyles({
  formGroup: {
    alignItems: 'flex-start',
    '& MuiCheckbox-root': {
      padding: 0
    }
  },
  formLabel: {
    color: neutral[600]
  },
  inputLabel: {
    marginLeft: 0,
    '& .MuiFormControlLabel-label': {
      fontFamily: fontFamilies.heading,
      fontSize: 14,
      lineHeight: 1.25
    },
    '& .MuiIconButton-root': {
      padding: 4
    }
  },
  selectActions: {
    fontFamily: fontFamilies.heading,
    fontSize: 11,
    color: neutral[500],
    padding: '0 4px',
    '& p': {
      margin: 0
    }
  },
  selectAllNone: {
    height: 30
  },
  noFilters: {
    ...TextStyles.label1
  }
});

/**
 * Render a section of filter options in the fitler sidebar
 */
const FilterGroup = ({ filters, title, selected, updateSelectedFilters }: FilterGroupProps) => {
  const classes = useStyles();

  const [checked, setChecked] = React.useState<Array<string | number>>(selected);

  React.useEffect(() => {
    setChecked(selected);
  }, []);

  // selected can change via cancel button in parent
  React.useEffect(() => {
    setChecked(selected);
  }, [selected]);

  const handleCheckToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    // check if ids are numerical to maintain type after checking
    // against stringified version (checkbox name attribute must be string)
    const idtype = filters && filters.length > 0 ? typeof filters[0].id : null;
    const idString = idtype == 'string' ? event.target.name : Number(event.target.name);
    const tempChecked = [...checked];
    const curidx = tempChecked.indexOf(idString);

    if (curidx == -1) {
      // convert back to number if id type is number
      if (idtype == 'string') {
        tempChecked.push(event.target.name);
      } else if (idtype == 'number') {
        tempChecked.push(Number(event.target.name));
      }
    } else {
      tempChecked.splice(curidx, 1);
    }
    setChecked(tempChecked);
    updateSelectedFilters(title, tempChecked);
  };

  const selectAll = () => {
    const tempChecked = filters.map((filter) => filter.id);
    setChecked(tempChecked);
    updateSelectedFilters(title, tempChecked);
  };
  const selectNone = () => {
    setChecked([]);
    updateSelectedFilters(title, []);
  };

  return (
    <FormControl component="fieldset">
      <FormLabel component="legend" className={classes.formLabel}>
        {title}
      </FormLabel>
      <FormGroup className={classes.formGroup}>
        <div className={classes.selectActions}>
          <p>
            Select:
            <TertiaryButton
              component="button"
              text="All"
              title="All"
              handleClick={(event) => selectAll()}
              className={classes.selectAllNone}
            />
            |
            <TertiaryButton
              component="button"
              text="None"
              title="None"
              handleClick={(event) => selectNone()}
              className={classes.selectAllNone}
            />
          </p>
        </div>
        {filters.length ? (
          filters.map((filter, idx) => (
            <FormControlLabel
              key={`filter-${idx}`}
              control={
                <StyledCheckbox
                  checked={checked.includes(filter.id)}
                  onChange={(event) => {
                    handleCheckToggle(event);
                  }}
                  name={String(filter.id)}
                />
              }
              label={filter.name}
              className={classes.inputLabel}
            />
          ))
        ) : (
          <p className={classes.noFilters}>No filters available</p>
        )}
      </FormGroup>
    </FormControl>
  );
};

export default FilterGroup;
